import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowRight, CalendarCheck, Inbox, Loader2, Mail, MessageCircle,
} from 'lucide-react';
import { getWebsiteEngagementSummary } from '@/lib/api';
import WebsiteEngagementShell from '@/components/admin/WebsiteEngagementShell';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

/**
 * The area's landing page: one card per inbox, each leading with the number
 * that means somebody has to do something.
 *
 * "Attention" rather than "total" is the headline on every card on purpose. A
 * total tells you how much has ever arrived; what a person opening this page
 * wants to know is what is waiting for them right now.
 */

const EMPTY = {
  recent_days: 30,
  messages: { total: 0, unread: 0, needs_staff: 0, email_follow_up: 0, attention: 0 },
  inquiries: { total: 0, new: 0, open: 0, replied: 0, closed: 0, spam: 0, unread: 0, unassigned: 0 },
  newsletter: { total: 0, active: 0, unsubscribed: 0 },
  signups: { total: 0, recent: 0, new: 0 },
  staff_online: false,
};

function InboxCard({
  label, to, icon: Icon, tone, headline, headlineLabel, secondary, loading, testId,
}) {
  return (
    <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm transition-shadow hover:shadow-md">
      <CardContent className="flex h-full flex-col p-5">
        <div className="mb-3 flex items-start justify-between gap-3">
          <span className="text-xs font-semibold uppercase tracking-[0.1em] text-[#062B1A]/50">
            {label}
          </span>
          <span className={cn('rounded-full p-2', tone)}>
            <Icon className="size-4" />
          </span>
        </div>
        {loading ? (
          <div className="flex flex-1 items-center gap-2 text-sm text-[#062B1A]/45">
            <Loader2 className="size-4 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            <p
              data-testid={testId}
              className="text-3xl font-bold text-[#062B1A]"
              style={{ fontFamily: 'var(--font-display)' }}
            >
              {headline}
            </p>
            <p className="mt-1 text-sm text-[#062B1A]/60">{headlineLabel}</p>
            <ul className="mt-3 space-y-1 text-xs text-[#062B1A]/55">
              {secondary.map((item) => (
                <li key={item.label} className="flex items-center justify-between gap-3">
                  <span>{item.label}</span>
                  <span className="font-semibold text-[#062B1A]/75">{item.value}</span>
                </li>
              ))}
            </ul>
          </>
        )}
        <Link
          to={to}
          className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#062B1A] underline-offset-4 hover:underline"
        >
          Open
          <ArrowRight className="size-3.5" />
        </Link>
      </CardContent>
    </Card>
  );
}

export default function WebsiteEngagementOverview() {
  const [summary, setSummary] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setFailed(false);
      const { data } = await getWebsiteEngagementSummary();
      setSummary({
        ...EMPTY,
        ...data,
        messages: { ...EMPTY.messages, ...(data?.messages || {}) },
        inquiries: { ...EMPTY.inquiries, ...(data?.inquiries || {}) },
        newsletter: { ...EMPTY.newsletter, ...(data?.newsletter || {}) },
        signups: { ...EMPTY.signups, ...(data?.signups || {}) },
      });
    } catch (error) {
      console.error(error);
      setFailed(true);
      toast.error(error?.response?.data?.detail || 'Could not load the engagement summary');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const { messages, inquiries, newsletter, signups } = summary;

  return (
    <WebsiteEngagementShell
      title="Overview"
      description={
        'Everything the public website sends us. Live chats, contact-form messages, '
        + 'newsletter opt-ins and event registrations are four separate records — this is '
        + 'what is waiting in each of them.'
      }
      actions={(
        <span
          data-testid="presence-indicator"
          title="What the public website shows visitors right now"
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium',
            summary.staff_online
              ? 'border-[rgba(34,197,94,0.25)] bg-[rgba(34,197,94,0.10)] text-[rgb(21,128,61)]'
              : 'border-[rgba(6,43,26,0.12)] bg-[rgba(6,43,26,0.04)] text-[#062B1A]/60',
          )}
        >
          <span className={cn(
            'size-2 rounded-full',
            summary.staff_online ? 'bg-[rgb(21,128,61)]' : 'bg-[#062B1A]/35',
          )}
          />
          {summary.staff_online ? 'Chat online' : 'Chat offline'}
        </span>
      )}
    >
      {failed && !loading && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
          <p>The summary could not be loaded. The four inboxes below still open normally.</p>
          <button
            type="button"
            onClick={load}
            className="mt-3 rounded-md border border-red-300 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100"
          >
            Try again
          </button>
        </div>
      )}

      <section
        aria-label="Website engagement summary"
        className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4"
        data-testid="engagement-summary"
      >
        <InboxCard
          label="Online Chat Messages"
          to="/admin/website-messages"
          icon={MessageCircle}
          tone="bg-[#D4AF37]/12 text-[#7A5A05]"
          loading={loading}
          testId="messages-attention"
          headline={messages.attention}
          headlineLabel="unread or waiting for a person"
          secondary={[
            { label: 'Unread', value: messages.unread },
            { label: 'Asked for a person', value: messages.needs_staff },
            { label: 'Email follow-up', value: messages.email_follow_up },
          ]}
        />
        <InboxCard
          label="Contact Form Inquiries"
          to="/admin/website-engagement/contact-inquiries"
          icon={Inbox}
          tone="bg-sky-50 text-sky-700"
          loading={loading}
          testId="inquiries-new"
          headline={inquiries.new}
          headlineLabel="new, not yet picked up"
          secondary={[
            { label: 'Open', value: inquiries.open },
            { label: 'Unread', value: inquiries.unread },
            { label: 'Nobody assigned', value: inquiries.unassigned },
          ]}
        />
        <InboxCard
          label="Newsletter"
          to="/admin/website-engagement/newsletter"
          icon={Mail}
          tone="bg-emerald-50 text-emerald-700"
          loading={loading}
          testId="newsletter-active"
          headline={newsletter.active}
          headlineLabel="active subscribers"
          secondary={[
            { label: 'Unsubscribed', value: newsletter.unsubscribed },
            { label: 'Total on record', value: newsletter.total },
          ]}
        />
        <InboxCard
          label="Event Signups"
          to="/admin/website-signups"
          icon={CalendarCheck}
          tone="bg-amber-50 text-amber-700"
          loading={loading}
          testId="signups-recent"
          headline={signups.recent}
          headlineLabel={`in the last ${summary.recent_days} days`}
          secondary={[
            { label: 'Awaiting contact', value: signups.new },
            { label: 'Total registrations', value: signups.total },
          ]}
        />
      </section>
    </WebsiteEngagementShell>
  );
}
